import {
  CheckCircle,
  Description,
  PlayCircleOutline,
  Star,
  WorkspacePremium,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Card,
  Grid,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ResourceDialog from "../components/dialogs/ResourceDialog";
import { StripeDialog } from "../components/dialogs/StripeDialog";
import { UpgradeVipDialog } from "../components/dialogs/UpgradeVipDialog";
import { PlanUpgradeBanner } from "../components/elements/PlanUpgradeBanner";
import { FormStatusSection } from "../components/metrics/FormStatusSection";
import { PlansSelection } from "../components/metrics/PlansSelectionProps";
import { PlanResource } from "../data/models/Plan/PlanModel";
import { useUserData } from "../data/providers/UserDataProvider";
import { usePlan } from "./hooks/usePlan";

const PlanPage: React.FC = () => {
  const [showPlanWelcome, setShowPlanWelcome] = useState(false);
  const { plan, loading, plans } = usePlan();
  const userData = useUserData();
  const [open, setOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<PlanResource | null>(
    null
  );
  const [openVipDialog, setOpenVipDialog] = useState(false);

  const vipPlan = plans?.find((e) => e.code === "PLAN_PRO")!;
  const kitPlan = plans?.find((e) => e.code === "MY_KIT")!;

  const [openStripe, setOpenStripe] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);

  useEffect(() => {
    if (!plans) return;
    getPlanAndExpire();
  }, [plans]);

  useEffect(() => {
    if (!plan) return;
    if (!plan.plan?.createdAt) return;

    if (isRecentPlan(plan.activatedAt)) {
      setShowPlanWelcome(true);
    }
  }, [plan]);

  const isRecentPlan = (createdAt: string) => {
    const created = Date.parse(createdAt);
    const nowLocal = Date.now();
    const offsetMinutes = new Date().getTimezoneOffset();
    const nowUtc = nowLocal - offsetMinutes * 60 * 1000;
    return nowUtc - created <= 350000;
  };

  const getPlanAndExpire = () => {
    const defaultPlan = localStorage.getItem("default_plan");
    if (!defaultPlan) return null;
    if (plan) {
      localStorage.removeItem("default_plan");
    } else {
      try {
        const _actionPlan = plans?.find((e) => e.code === defaultPlan)!;
        setSelectedPlan(null);
        setSelectedPlan(_actionPlan);
        setOpenStripe(true);
        localStorage.removeItem("default_plan");
      } catch (error) {
        console.error(error);
      }
    }
  };

  if (loading) return <Typography>Cargando...</Typography>;

  const handleOpenResource = (resource: PlanResource) => {
    setSelectedResource(resource);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedResource(null);
  };

  if (!plan) {
    return (
      <>
        <PlansSelection
          plans={plans!}
          recommendedCode="MY_KIT"
          onSelect={(plan) => {
            setSelectedPlan(null);
            setSelectedPlan(plan);
            setOpenStripe(true);
          }}
        />

        {selectedPlan && (
          <StripeDialog
            open={openStripe}
            onClose={() => {
              setOpenStripe(false);
              setSelectedPlan(null);
            }}
            planCode={selectedPlan.code}
            amountUsd={selectedPlan.priceUsd}
          />
        )}
      </>
    );
  }

  const mediaResources = plan.plan.capacities.flatMap((c) =>
    c.resources.filter((r) => r.resourceType === "VIDEO")
  );
  const fileResources = plan.plan.capacities.flatMap((c) =>
    c.resources.filter((r) =>
      ["PDF", "MANUAL", "LINK"].includes(r.resourceType)
    )
  );

  const StepHeader = ({ step, title }: { step: number; title: string }) => (
    <Box
      sx={{
        mb: 3,
        display: "flex",
        alignItems: "center",
        gap: 2.2,
      }}
    >
      <Box
        sx={{
          minWidth: 40,
          height: 40,
          borderRadius: "12px",
          background: "linear-gradient(135deg, #0E1EFD 0%, #0090FF 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: 16,
          fontWeight: 700,
          boxShadow: "0 6px 18px rgba(0,70,180,0.25)",
        }}
      >
        {step}
      </Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            fontSize: 13,
            textTransform: "uppercase",
            color: "primary.main",
            fontWeight: 700,
            opacity: 0.85,
            letterSpacing: 1,
          }}
        >
          Paso {step}
        </Typography>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mt: "-2px",
            background:
              "linear-gradient(90deg, #002BFF 0%, #007BFF 70%, #00A8FF 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            fontFamily: "Montserrat, sans-serif",
          }}
        >
          {title}
        </Typography>
      </Box>
    </Box>
  );

  const bgColor =
    plan.planCode === "MY_KIT" ? "rgba(255,255,255,0.15)" : "#ffd23eb2";

  return (
    <Box sx={{ p: 3 }}>
      {plan.planCode === "MY_KIT" && (
        <PlanUpgradeBanner
          superiorPlan={vipPlan}
          onUpgrade={() => {
            setOpenVipDialog(true);
          }}
        />
      )}

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          mb: 4,
          p: { xs: 3, sm: 4 },
          position: "relative",
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #1422e9ff 0%, #4671ffff 40%, #83aafdff 100%)",
          color: "#fff",
          boxShadow: "0 10px 30px rgba(77, 102, 245, 0.35)",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: 2, sm: 3 },
          "&::before": {
            content: '""',
            position: "absolute",
            top: "-40%",
            left: "-20%",
            width: "160%",
            height: "200%",
            background:
              "radial-gradient(circle at top left, rgba(255,255,255,0.25), transparent 70%)",
            transform: "rotate(25deg)",
            zIndex: 0,
          },
        }}
      >
        <Box
          sx={{
            width: { xs: 60, sm: 80 },
            height: { xs: 60, sm: 80 },
            borderRadius: "50%",
            background: bgColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 20px rgba(255,255,255,0.2)",
            flexShrink: 0,
            zIndex: 1,
          }}
        >
          <WorkspacePremium
            sx={{ fontSize: { xs: 36, sm: 48 }, color: "#ffffffff" }}
          />
        </Box>

        <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              color: "#fff",
              fontSize: { xs: "1.4rem", sm: "1.8rem" },
              mb: 0.5,
              lineHeight: 1.2,
            }}
          >
            {plan.plan.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              opacity: 0.9,
              fontSize: { xs: "0.85rem", sm: "1rem" },
            }}
          >
            Tu suscripción actual
          </Typography>
        </Box>

        <Box
          sx={{
            position: "absolute",
            right: { xs: 10, sm: 20 },
            bottom: { xs: 8, sm: 15 },
            opacity: 0.25,
          }}
        >
          <Star sx={{ fontSize: { xs: 50, sm: 90 }, color: "#fff" }} />
        </Box>
      </Card>

      <StepHeader step={1} title="Videos del kit" />

      {mediaResources.length === 0 ? (
        <Typography color="text.secondary" variant="body2" mb={4}>
          No hay videos disponibles en tu plan.
        </Typography>
      ) : (
        <Grid container spacing={3} marginBottom={3}>
          {mediaResources.map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <Card
                onClick={() => handleOpenResource(r)}
                sx={{
                  cursor: "pointer",
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    bgcolor: "grey.900",
                    height: 120,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PlayCircleOutline
                    sx={{
                      fontSize: 60,
                      color: "white",
                      opacity: 0.8,
                      transition: "opacity 0.3s",
                      "&:hover": { opacity: 1 },
                    }}
                  />
                </Box>

                <Box sx={{ p: 2 }}>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.primary"
                    noWrap
                  >
                    {r.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Video del kit
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <StepHeader step={2} title="Manual" />

      {fileResources.length === 0 ? (
        <Typography color="text.secondary" variant="body2">
          No hay archivos disponibles en tu plan.
        </Typography>
      ) : (
        <Grid container spacing={3} marginBottom={3}>
          {fileResources.map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <Card
                onClick={() => handleOpenResource(r)}
                sx={{
                  cursor: "pointer",
                  p: 3,
                  borderRadius: 4,
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  boxShadow: "0 3px 10px rgba(0,0,0,0.06)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 6px 18px rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Description
                  sx={{
                    fontSize: 40,
                    color: "secondary.main",
                    opacity: 0.8,
                  }}
                />
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    color="text.primary"
                    noWrap
                  >
                    {r.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Documento PDF
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {plan.planCode === "PLAN_PRO" && (
        <Stack>
          <StepHeader step={3} title="Formulario para llenar DS-160" />
          <FormStatusSection
            submission={userData?.data?.submission}
            onCloseForm={() => userData?.reloadUserData()}
            authToken={userData?.data?.userId!}
          />
        </Stack>
      )}

      <ResourceDialog
        open={open}
        onClose={handleClose}
        resource={selectedResource}
      />

      {selectedPlan && (
        <StripeDialog
          open={openStripe}
          onClose={() => setOpenStripe(false)}
          planCode={selectedPlan.code}
          amountUsd={selectedPlan.priceUsd}
        />
      )}

      <Snackbar
        open={showPlanWelcome}
        autoHideDuration={4000}
        onClose={() => setShowPlanWelcome(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowPlanWelcome(false)}
          severity="success"
          variant="filled"
          icon={<CheckCircle fontSize="inherit" />}
          sx={{ fontSize: "1rem", fontWeight: 600 }}
        >
          Plan activado: <strong>{plan.plan.name}</strong> ✅
        </Alert>
      </Snackbar>

      <UpgradeVipDialog
        open={openVipDialog}
        onClose={() => setOpenVipDialog(false)}
        vipPlan={vipPlan}
        kitPlan={kitPlan}
        onConfirm={() => {
          setOpenVipDialog(false);
          setSelectedPlan(null);
          setSelectedPlan(vipPlan);
          setOpenStripe(true);
        }}
      />
    </Box>
  );
};

export default PlanPage;
